import React, { useState } from "react";
import { AbsoluteFill, Img, staticFile, useCurrentFrame, useVideoConfig, interpolate, Easing } from "remotion";
import { z } from "zod";

// Zod schema for MagnifyingGlass props
const MagnifyingGlassPropsSchema = z.object({
  imageSrc: z.string().default("1.jpeg"),
  magnifierX: z.number().min(0).max(100).default(50), // X position as percentage (0-100)
  magnifierY: z.number().min(0).max(100).default(50), // Y position as percentage (0-100)
  magnifierRadius: z.number().min(50).max(500).default(150), // Radius in pixels
  magnificationLevel: z.number().min(1.5).max(5).default(2.5), // How much to magnify
  borderWidth: z.number().min(0).max(20).default(8), // Border thickness
  borderColor: z.string().default("#ffffff"), // Border color
  shadowBlur: z.number().min(0).max(50).default(20), // Shadow blur radius
  shadowColor: z.string().default("rgba(0, 0, 0, 0.3)"), // Shadow color
  backgroundZoomStart: z.number().min(1).max(2).default(1), // Starting zoom level
  backgroundZoomEnd: z.number().min(1.1).max(3).default(1.5), // Ending zoom level
  backgroundZoomDuration: z.number().min(1).max(1800).default(600), // Duration of zoom in frames
  easingType: z.enum(["linear", "ease-in", "ease-out", "ease-in-out"]).default("ease-in-out"),
});

export { MagnifyingGlassPropsSchema };

/**
 * MagnifyingGlass Component
 * - Creates a circular magnifying glass effect on an image
 * - Magnifier position is modular (0-100% on both axes)
 * - Background image slowly zooms in over time
 * - Customizable magnification level, size, and styling
 * - Smooth easing options for zoom animation
 */
export const MagnifyingGlass = ({
  imageSrc = "1.jpeg",
  magnifierX = 50,
  magnifierY = 50,
  magnifierRadius = 150,
  magnificationLevel = 2.5,
  borderWidth = 8,
  borderColor = "#ffffff",
  shadowBlur = 20,
  shadowColor = "rgba(0, 0, 0, 0.3)",
  backgroundZoomStart = 1,
  backgroundZoomEnd = 1.5,
  backgroundZoomDuration = 600,
  easingType = "ease-in-out",
}) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  // Track image error state
  const [imageError, setImageError] = useState(false);
  const [imageErrorMessage, setImageErrorMessage] = useState('');

  // Helper function to get image source (local file or remote URL)
  const getImageSrc = (imgPath) => {
    // Check if it's a URL (starts with http:// or https://)
    if (imgPath.startsWith('http://') || imgPath.startsWith('https://')) {
      return imgPath;
    }
    // Otherwise, treat as local file from public folder
    return staticFile(imgPath);
  };

  // Handle image errors
  const handleImageError = (error) => {
    console.error('Image load error in MagnifyingGlass:', error);
    setImageError(true);
    setImageErrorMessage(error?.message || 'Failed to load image');
  };

  // Get easing function based on type
  const getEasingFunction = (type) => {
    switch (type) {
      case "linear": return Easing.linear;
      case "ease-in": return Easing.in(Easing.quad);
      case "ease-out": return Easing.out(Easing.quad);
      case "ease-in-out": return Easing.inOut(Easing.quad);
      default: return Easing.inOut(Easing.quad);
    }
  };

  // Calculate background zoom scale
  const zoomProgress = Math.min(frame / backgroundZoomDuration, 1);
  const backgroundScale = interpolate(
    zoomProgress,
    [0, 1],
    [backgroundZoomStart, backgroundZoomEnd],
    {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
      easing: getEasingFunction(easingType),
    }
  );

  // Convert percentage positions to pixel coordinates
  // Magnifier stays in fixed screen position with original image scale
  const magnifierCenterX = (magnifierX / 100) * width;
  const magnifierCenterY = (magnifierY / 100) * height;
  
  // Calculate offset to center the magnified content in the circular magnifier
  // Shows original image scale - completely independent of background zoom
  const magnifiedOffsetX = -(magnifierCenterX * magnificationLevel - magnifierRadius);
  const magnifiedOffsetY = -(magnifierCenterY * magnificationLevel - magnifierRadius);

  return (
    <AbsoluteFill style={{ backgroundColor: "#000", overflow: "hidden" }}>
      {imageError ? (
        // Show error fallback instead of crashing
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#000",
            color: "#fff",
            fontFamily: "Arial, sans-serif",
            padding: "40px",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 20 }}>⚠️</div>
          <div style={{ fontSize: 24, marginBottom: 10 }}>Image Error</div>
          <div style={{ fontSize: 16, color: "#999", maxWidth: 600 }}>
            {imageErrorMessage || 'Unable to load image'}
          </div>
          <div style={{ fontSize: 14, color: "#666", marginTop: 20 }}>
            Source: {imageSrc}
          </div>
        </div>
      ) : (
        <>
          {/* Background image with slow zoom */}
          <div
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              transform: `scale(${backgroundScale})`,
              transformOrigin: "center center",
            }}
          >
            <Img
              src={getImageSrc(imageSrc)}
              onError={handleImageError}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </div>

          {/* Magnifying glass effect */}
          <div
            style={{
              position: "absolute",
              left: magnifierCenterX - magnifierRadius,
              top: magnifierCenterY - magnifierRadius,
              width: magnifierRadius * 2,
              height: magnifierRadius * 2,
              borderRadius: "50%",
              border: `${borderWidth}px solid ${borderColor}`,
              boxShadow: `0 0 ${shadowBlur}px ${shadowColor}`,
              overflow: "hidden",
              zIndex: 10,
            }}
          >
            {/* Magnified image - shows original zoom level, independent of background animation */}
            <div
              style={{
                position: "absolute",
                width: width * magnificationLevel,
                height: height * magnificationLevel,
                left: magnifiedOffsetX,
                top: magnifiedOffsetY,
                // No transform applied - keeps original image scale regardless of background zoom
              }}
            >
              <Img
                src={getImageSrc(imageSrc)}
                onError={handleImageError}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </div>
          </div>

          {/* Optional: Add a subtle highlight ring inside the magnifier */}
          <div
            style={{
              position: "absolute",
              left: magnifierCenterX - magnifierRadius,
              top: magnifierCenterY - magnifierRadius,
              width: magnifierRadius * 2,
              height: magnifierRadius * 2,
              borderRadius: "50%",
              border: "2px solid rgba(255, 255, 255, 0.2)",
              pointerEvents: "none",
              zIndex: 11,
            }}
          />
        </>
      )}
    </AbsoluteFill>
  );
};

export default MagnifyingGlass;

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
